import { FontMetrics, getFontMetrics } from "../../util/font";
import { FontData, Region, SizeRange } from "../../types";
import { fontSizeStep, textWidth } from "../shared";

/**
 * Produce an array where text has been broken into lines that can
 * fit the region at the current size.
 * If it can't be done then return null;
 */
const linesThatFit = (
    region: Region,
    linesToBreak: string[],
    fontSize: number,
    fontScale: number,
    cache: FontMetrics,
): [null | string[], null | number] => {
    if (linesToBreak.some(line => line === undefined || line === null)) {
        throw new Error(`Failed to autosize lines: ${linesToBreak.join(", ")}`);
    }
    let lines = linesToBreak.map(line => line.split("\n")).flat();
    let amountLines = lines.length;
    let widthsPerLine = lines.map((line) => textWidth(line, fontScale, cache));
    const lineHeight = cache.getApproximateHeight() * fontScale;

    let couldStillFit = true;
    while (couldStillFit) {
        // Determine if textbox would be too high.
        // If the textbox is too high, line breaking isn't going to help.
        // Height of the textbox is the height of the last line plus its vertical offset.
        const textboxHeight = lineHeight + (amountLines - 1) * fontSize;
        if (textboxHeight > region.height) {
            return [null, null];
        }

        // Determine if textbox would be too wide.
        // It's not too high, so if it's not too wide then we've succeeded.
        // Width of the whole textbox is the width of the longest line.
        const textboxWidth = Math.max(...widthsPerLine);
        if (textboxWidth <= region.width) {
            return [lines, textboxWidth];
        }

        // It's too wide. Find the widest line and look for a space.
        const idxWidestLine = widthsPerLine.reduce((iMax, x, i, arr) => (x > arr[iMax] ? i : iMax), 0);
        const widestLine = lines[idxWidestLine];

        // Find the latest space to split at that makes the current widest line fit.
        let foundGoodSpacing = false;
        let idxOfSpace = widestLine.length;
        while (!foundGoodSpacing && idxOfSpace > -1) {
            idxOfSpace = widestLine.lastIndexOf(" ", idxOfSpace - 1);
            const splitLine = [widestLine.slice(0, idxOfSpace), widestLine.slice(idxOfSpace + 1)];
            const splitWidths = splitLine.map((line) => textWidth(line, fontScale, cache));
            const firstWidth = splitWidths[0];
            if (firstWidth <= region.width) {
                lines = [...lines.slice(0, idxWidestLine), ...splitLine, ...lines.slice(idxWidestLine + 1)];
                widthsPerLine = [
                    ...widthsPerLine.slice(0, idxWidestLine),
                    ...splitWidths,
                    ...widthsPerLine.slice(idxWidestLine + 1),
                ];
                amountLines += 1;
                foundGoodSpacing = true;
            }
        }
        if (!foundGoodSpacing) {
            couldStillFit = false;
        }
    }

    // No way to fit.
    return [null, null];
};

/**
 * Determine what the font size for a text element should be,
 * taking autosizing into account.
 * @Deprecated Old Text system, Transition to new system when Autosizing has been brought across in a way that makes sense.
 */
export const determineCorrectFontSizeAndLines = (
    curFontSize: number,
    fontData: FontData,
    region: Region,
    linesToBreak: string[],
    sizeRange: SizeRange,
): [number, null | string[], null | number] => {
    let lines;
    let requiredWidth;

    const cache = getFontMetrics(fontData.assetUrl);
    const font = cache.getFont();

    // Fixed Mode: Text is computed based on fixed text size.
    if (sizeRange.size) {
        const fontScale = curFontSize / font.unitsPerEm;
        [lines, requiredWidth] = linesThatFit(region, linesToBreak, curFontSize, fontScale, cache);
        return [sizeRange.size, lines, requiredWidth];
    }

    // Variable Mode: Text is computed based on a range
    const nonUserMin = 6;
    let calculatedFontSize = nonUserMin - fontSizeStep;
    if (linesToBreak.length > 0) {
        let lines: null | string[] = linesToBreak;
        let fontScale = 0;
        while ((!sizeRange.maxSize || calculatedFontSize <= sizeRange.maxSize) && lines) {
            calculatedFontSize += fontSizeStep;
            fontScale = calculatedFontSize / font.unitsPerEm;
            [lines, requiredWidth] = linesThatFit(region, linesToBreak, calculatedFontSize, fontScale, cache);
        }
    }
    if (calculatedFontSize > nonUserMin) {
        // Correct for final enlargement.
        calculatedFontSize -= fontSizeStep;
    }
    if (sizeRange.minSize && calculatedFontSize < sizeRange.minSize) {
        // Correct for minimum font size.
        calculatedFontSize = sizeRange.minSize;
    }
    const fontScale = calculatedFontSize / font.unitsPerEm;
    [lines, requiredWidth] = linesThatFit(region, linesToBreak, calculatedFontSize, fontScale, cache);
    return [calculatedFontSize, lines, requiredWidth];
};

// https://stackoverflow.com/a/22015771
const zip = (xs: any[], ys: any[]) => {
    return xs.map((x, idx) => [x, ys[idx]]);
};

/**
 * If a textbox has a fixed font size, it's width should be changed to fit the text.
 * Needs to take rotation into consideration.
 * The left alignment case appears to be resolved but the other cases have not been addressed.
 * @Deprecated Old Text system, Transition to new system when Autosizing has been brought across in a way that makes sense.
 */
export const determineCorrectTextboxRegion = (
    currentRegion: Region,
    fontData: FontData,
    fontSize: number,
    textLines: string[],
    align?: string,
): Region => {
    let region = { ...currentRegion };
    const candidateRegion = { ...currentRegion };
    // requiredWidth could be const but the others must be let.
    // eslint-disable-next-line prefer-const
    let [calculatedFontSize, goodLines, requiredWidth] = determineCorrectFontSizeAndLines(
        fontSize,
        fontData,
        candidateRegion,
        textLines,
        { size: 0, minSize: fontSize, maxSize: fontSize },
    );
    if (requiredWidth && requiredWidth < region.width) {
        candidateRegion.width = requiredWidth;
        if (align === "left") {
            candidateRegion.left += Math.sin((region.rotation * Math.PI) / 360) * (region.width - requiredWidth);
            candidateRegion.top += (Math.sin((region.rotation * Math.PI) / 180) * (requiredWidth - region.width)) / 2;
        } else if (align === "right") {
            candidateRegion.left += region.width - requiredWidth;
        } else {
            candidateRegion.left += (region.width - requiredWidth) / 2;
        }
        [calculatedFontSize, goodLines] = determineCorrectFontSizeAndLines(fontSize, fontData, candidateRegion, textLines, {
            size: fontSize,
        });
        if (
            calculatedFontSize === fontSize &&
            goodLines &&
            zip(textLines, goodLines).every(([lineA, lineB]) => lineA === lineB)
        ) {
            region = candidateRegion;
        }
    }
    return region;
};
